import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

interface AdSenseProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal' | 'vertical';
  responsive?: boolean;
  className?: string;
}

export const AdSense = ({ 
  slot, 
  format = 'auto', 
  responsive = true,
  className = '' 
}: AdSenseProps) => {
  const adRef = useRef<HTMLModElement>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Reset on mount to handle component remounting
    hasInitialized.current = false;
    
    const initAd = () => {
      try {
        // Check if ad element exists and hasn't been initialized
        if (adRef.current && !hasInitialized.current) {
          const adStatus = adRef.current.dataset.adsbygoogleStatus;
          
          // Only push if not already done
          if (!adStatus) {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            hasInitialized.current = true;
          }
        }
      } catch (err) {
        console.error('AdSense error:', err);
      }
    };

    // Wait for DOM to be ready and AdSense script to load
    const timer = setTimeout(initAd, 200);

    return () => {
      clearTimeout(timer);
    };
  }, [slot]); // Re-run if slot changes

  return (
    <div className={`ad-container ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-8119505360206029"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
};

// Horizontal banner ad - good for between sections
export const AdBanner = ({ slot, className = '' }: { slot: string; className?: string }) => (
  <div className={`w-full max-w-4xl mx-auto py-4 ${className}`}>
    <AdSense slot={slot} format="horizontal" className="min-h-[90px]" />
  </div>
);

// In-feed ad - blends with content lists
export const AdInFeed = ({ slot, className = '' }: { slot: string; className?: string }) => (
  <div className={`${className}`}>
    <AdSense slot={slot} format="fluid" className="min-h-[250px]" />
  </div>
);

// Sidebar ad - for detail pages
export const AdSidebar = ({ slot, className = '' }: { slot: string; className?: string }) => (
  <div className={`sticky top-24 ${className}`}>
    <p className="text-xs text-muted-foreground mb-2 text-center">Advertisement</p>
    <AdSense slot={slot} format="rectangle" className="min-h-[250px]" />
  </div>
);
